import React from "react"
import { Row, Col, Button, Form } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import FormikCreatableSelect from "../../Common/Formik/FormikCreatableSelect"
import FormikDatePicker from "../../Common/Formik/FormikDatePicker"
import FormikSelect from "../../Common/Formik/FormikSelect"
import FormikInput from "../../Common/Formik/FormikInput"
import FormikCurrencyInput from "../../Common/Formik/FormikCurrencyInput"
import Paginacion from "./Paginacion" // Control independiente para la paginación

const GastosForm = ({
  initialValues,
  modo = "captura", // "captura", "consulta", "edicion"
  onSubmit,
  onNextRecord,
  onPreviousRecord,
  isLoading = { isLoading },
  optionsPersonaRecibeRecurso = [],
  optionsPersonaEntregoRecurso = [],
  optionsPersonaAutorizoRecurso = [],
  documentosOptions = [],
  costosOptions = [],
  conceptosGastoOptions = [],
  formaPagoOptions = [],
  handleNextRecord,
  handlePreviousRecord,
}) => {
  const isReadOnly = modo === "consulta"

  const validationSchema = Yup.object().shape({
    Descripcion_Persona_Recibo: Yup.string().required("Campo requerido"),
    Fecha_Creacion: Yup.date().required("Campo requerido"),
    Importe: Yup.number().positive("El importe debe ser mayor a cero"),
    Descripcion_Operacion: Yup.string().required("Campo requerido"),
  })

  const formik = useFormik({
    initialValues: initialValues || {},
    validationSchema: validationSchema,
    onSubmit: onSubmit,
    enableReinitialize: true,
  })

  // #region functions
  const createOption = label => ({
    label,
    value: label.toLowerCase().replace(/\W/g, ""),
  })

  const handleCreatePersonaRecibeRecurso = inputValue => {
    setIsLoading(true)
    setTimeout(() => {
      const newOption = createOption(inputValue)
      setIsLoading(false)

      // Actualizamos las opciones en el estado
      setOptionsPersonaRecibeRecurso(prev => {
        const newOptions = [...prev, newOption]
        newOptions.sort((a, b) => a.label.localeCompare(b.label))
        return newOptions
      })
      setValuePersonaRecibeRecurso(newOption)

      // Actualizamos localStorage
      let stored = localStorage.getItem("CajaPersonaRecibeRecurso")
      let storedValues = []
      if (stored) {
        try {
          storedValues = JSON.parse(stored)
          if (!Array.isArray(storedValues)) {
            storedValues = []
          }
        } catch (e) {
          storedValues = []
        }
      }
      storedValues.push(inputValue)
      localStorage.setItem(
        "CajaPersonaRecibeRecurso",
        JSON.stringify(storedValues)
      )
    }, 1000)
  }

  const handleCreatePersonaEntregoRecurso = inputValue => {
    setIsLoading(true)
    setTimeout(() => {
      //
      const newOption = createOption(inputValue)
      setIsLoading(false)

      // Actualizamos las opciones en el estado
      setOptionsPersonaEntregoRecurso(prev => {
        const newOptions = [...prev, newOption]
        newOptions.sort((a, b) => a.label.localeCompare(b.label))
        return newOptions
      })
      setValuePersonaEntregoRecurso(newOption)

      // Actualizamos localStorage
      let stored = localStorage.getItem("CajaPersonaEntregoRecurso")
      let storedValues = []
      if (stored) {
        try {
          storedValues = JSON.parse(stored)
          if (!Array.isArray(storedValues)) {
            storedValues = []
          }
        } catch (e) {
          storedValues = []
        }
      }
      storedValues.push(inputValue)
      localStorage.setItem(
        "CajaPersonaEntregoRecurso",
        JSON.stringify(storedValues)
      )
    }, 1000)
  }

  const handleCreatePersonaAutorizoRecurso = inputValue => {
    if (regex.test(inputValue)) {
      setIsLoading(true)
      setTimeout(() => {
        const newOption = createOption(inputValue)
        setIsLoading(false)

        // Actualizamos las opciones en el estado
        setOptionsPersonaAutorizoRecurso(prev => {
          const newOptions = [...prev, newOption]
          newOptions.sort((a, b) => a.label.localeCompare(b.label))
          return newOptions
        })
        setValuePersonaAutorizoRecurso(newOption)

        // Actualizamos localStorage
        let stored = localStorage.getItem("CajaPersonaAutorizoRecurso")
        let storedValues = []
        if (stored) {
          try {
            storedValues = JSON.parse(stored)
            if (!Array.isArray(storedValues)) {
              storedValues = []
            }
          } catch (e) {
            storedValues = []
          }
        }
        storedValues.push(inputValue)
        localStorage.setItem(
          "CajaPersonaAutorizoRecurso",
          JSON.stringify(storedValues)
        )
      }, 1000)
    }
  }

  const handleUpperCaseChange = event => {
    // Obtiene el nombre del campo y el valor en mayúsculas
    const name = event.target.name
    const value = event.target.value.toUpperCase()

    // Utiliza Formik para actualizar el valor
    formik.setFieldValue(name, value)
  }

  // #endregion

  return (
    <Form onSubmit={formik.handleSubmit}>
      {/* Persona que recibió el recurso - Fecha */}
      <Row>
        <Col lg="6">
          <FormikCreatableSelect
            label="Persona que recibió el recurso"
            name="Descripcion_Persona_Recibo"
            formik={formik}
            options={optionsPersonaRecibeRecurso}
            isLoading={isLoading}
            onCreateOption={handleCreatePersonaRecibeRecurso}
            isDisabled={isReadOnly}
          />
        </Col>
        <Col sm="6">
          <FormikDatePicker
            label="Fecha:"
            name="Fecha_Creacion"
            placeholder="Fecha"
            dateFormat="Y-m-d"
            formik={formik}
            readOnly={isReadOnly}
          />
        </Col>
      </Row>

      {/* Comprobante del gasto - Folio */}
      <Row>
        <Col lg="6">
          <FormikSelect
            label="* Comprobante del gasto:"
            name="id_Documento"
            placeholder="Documento"
            options={documentosOptions || []}
            formik={formik}
            isDisabled={isReadOnly}
          />
        </Col>
        <Col sm="6">
          <FormikInput
            label="* Número / Folio:"
            name="Folio_Documento"
            placeholder="Folio"
            formik={formik}
            onChange={handleUpperCaseChange}
            readOnly={isReadOnly}
          />
        </Col>
      </Row>

      {/* Centro de costos - Importe */}
      <Row>
        <Col lg="6">
          <FormikSelect
            label="* Centro de costos:"
            name="id_Centro_Costos"
            placeholder="Centro de Costos"
            options={costosOptions || []}
            formik={formik}
            isDisabled={isReadOnly}
          />
        </Col>
        <Col sm="6">
          <FormikCurrencyInput
            label="* Importe"
            name="Importe"
            formik={formik}
            max={100000}
            showCharacterCounter={false}
            isLoading={isLoading}
            readOnly={isReadOnly}
          />
        </Col>
      </Row>

      {/* Concepto - Formas de Pago */}
      <Row>
        <Col lg="6">
          <FormikSelect
            label="* Concepto:"
            name="id_Concepto_Gasto"
            placeholder="Concepto Gasto"
            options={conceptosGastoOptions || []}
            formik={formik}
            isDisabled={isReadOnly}
          />
        </Col>
        <Col sm="6">
          <FormikSelect
            label="* Forma de pago:"
            name="id_Forma_Pago"
            placeholder="Forma de pago"
            options={formaPagoOptions || []}
            formik={formik}
            isLoading={isLoading}
            isDisabled={isReadOnly}
          />
        </Col>
      </Row>

      {/* Descripción */}
      <Row>
        <Col lg="12">
          <FormikInput
            label="Descripción"
            name="Descripcion_Operacion"
            placeholder=""
            formik={formik}
            onChange={handleUpperCaseChange}
            autoComplete="off"
            type="textarea"
            readOnly={isReadOnly}
          />
        </Col>
      </Row>

      {/* Entregó recurso */}
      <Row>
        <Col lg="12">
          <FormikCreatableSelect
            label="* Entregó recurso:"
            name="Descripcion_Persona_Entrego_Recibo"
            formik={formik}
            options={optionsPersonaEntregoRecurso}
            isLoading={isLoading}
            onCreateOption={handleCreatePersonaEntregoRecurso}
            isDisabled={isReadOnly}
          />
        </Col>
      </Row>

      {/* Autorizó */}
      <Row>
        <Col lg="12">
          <FormikCreatableSelect
            label="* Autorizó:"
            name="Descripcion_Persona_Autorizadora_Operacion"
            formik={formik}
            options={optionsPersonaAutorizoRecurso}
            isLoading={isLoading}
            onCreateOption={handleCreatePersonaAutorizoRecurso}
            isDisabled={isReadOnly}
          />
        </Col>
      </Row>

      {/* Botones de Acción según el modo */}
      <div className="d-flex justify-content-end mt-2">
        {modo === "captura" && (
          <Button type="submit" color="primary" disabled={!formik.isValid}>
            Registrar
          </Button>
        )}

        {modo === "edicion" && (
          <Button type="submit" color="warning" disabled={!formik.isValid}>
            Actualizar
          </Button>
        )}

        {modo === "consulta" && (
          <div className="d-flex gap-2">
            <Button color="secondary" onClick={handlePreviousRecord}>
              Anterior
            </Button>
            <Button color="secondary" onClick={handleNextRecord}>
              Siguiente
            </Button>
          </div>
        )}
      </div>
    </Form>
  )
}

export default GastosForm
